import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Radio,
  Row,
  Col,
  Divider,
  Button,
  Space,
  message,
  Card,
  Typography,
  Alert,
} from "antd";
import {
  BarcodeOutlined,
  DeploymentUnitOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import traceabilityService from "../../../services/traceabilityService";
import { TTraceableInstance } from "../../../models/traceabilityModel";
import { ItemSearchSelect } from "../../common/ItemSearchSelect";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tokenData: any;
  companyUid: string;
  companyId: number;
  categories: any[];
  storehouses: any[];
  storageElements: any[];
  groups: any[];
  editingInstance?: TTraceableInstance | null;
}

export const RegisterTraceableInstanceModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
  tokenData,
  companyUid,
  companyId,
  categories,
  storehouses,
  storageElements,
  groups,
  editingInstance,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [creationMode, setCreationMode] = useState<"SINGLE" | "BULK">("SINGLE");
  const selectedCategoryOptionId = Form.useWatch("category_option_id", form);

  // Determine category key
  const selectedCategory = categories.find((c) => c.id === selectedCategoryOptionId);
  const categoryKey = selectedCategory?.key || "CUSTOM";

  useEffect(() => {
    if (visible) {
      if (editingInstance) {
        setCreationMode("SINGLE");
        form.setFieldsValue({
          identifier: editingInstance.identifier,
          name: editingInstance.name,
          element_id: editingInstance.element_id,
          category_option_id: editingInstance.category_option_id,
          status: editingInstance.status,
          storehouse_id: editingInstance.storehouse_id,
          location: editingInstance.location,
          entry_date: editingInstance.entry_date ? dayjs(editingInstance.entry_date) : dayjs(),
          metadata: editingInstance.metadata || {},
          group_id: editingInstance.traceable_instance_group_members?.find((m) => m.is_active)?.group_id,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: "ACTIVE",
          entry_date: dayjs(),
          category_option_id: categories.length > 0 ? categories[0].id : undefined,
        });
      }
    }
  }, [visible, editingInstance, categories]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const baseInstanceData = {
        company_id: companyId,
        element_id: values.element_id || null,
        category_option_id: values.category_option_id || null,
        status: values.status || "ACTIVE",
        storehouse_id: values.storehouse_id || null,
        location: values.location || null,
        entry_date: values.entry_date ? values.entry_date.toISOString() : new Date().toISOString(),
        metadata: values.metadata || {},
      };

      if (editingInstance) {
        // Edit single
        const res = await traceabilityService.updateInstance(tokenData, {
          company_uid: companyUid,
          instance: {
            ...baseInstanceData,
            id: editingInstance.id,
            identifier: values.identifier,
            name: values.name || null,
          },
        });

        if (res.error) throw new Error(res.message);
        message.success("Elemento trazable actualizado con éxito");
      } else if (creationMode === "SINGLE") {
        // Create single
        const res = await traceabilityService.createInstance(tokenData, {
          company_uid: companyUid,
          group_id: values.group_id || null,
          instance: {
            ...baseInstanceData,
            identifier: values.identifier,
            name: values.name || null,
          },
        });

        if (res.error) throw new Error(res.message);
        message.success("Elemento trazable registrado con éxito");
      } else {
        // Create BULK
        const identifiersList: string[] = values.bulk_identifiers
          ? values.bulk_identifiers
              .split("\n")
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0)
          : [];

        if (identifiersList.length === 0) {
          throw new Error("Debe ingresar al menos un identificador para la creación en lote");
        }

        const instancesPayload = identifiersList.map((idStr: string) => ({
          ...baseInstanceData,
          identifier: idStr,
          name: values.bulk_name_prefix ? `${values.bulk_name_prefix} ${idStr}` : null,
        }));

        const res = await traceabilityService.createInstancesBulk(tokenData, {
          company_uid: companyUid,
          company_id: companyId,
          group_id: values.group_id || null,
          instances: instancesPayload,
        });

        if (res.error) throw new Error(res.message);
        message.success(`${res.count || identifiersList.length} elementos trazables creados con éxito`);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      message.error(err.message || "Error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <Space align="center">
          <DeploymentUnitOutlined style={{ color: "#1890ff", fontSize: 20 }} />
          <span>{editingInstance ? `Editar Elemento: ${editingInstance.identifier}` : "Nuevo Elemento Trazable"}</span>
        </Space>
      }
      width={780}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {editingInstance ? "Guardar Cambios" : creationMode === "BULK" ? "Crear Elementos en Lote" : "Registrar Elemento"}
        </Button>,
      ]}
      destroyOnHidden
    >
      {!editingInstance && (
        <div style={{ marginBottom: 16, textAlign: "center" }}>
          <Radio.Group
            value={creationMode}
            onChange={(e) => setCreationMode(e.target.value)}
            buttonStyle="solid"
            size="middle"
          >
            <Radio.Button value="SINGLE">Registro Individual</Radio.Button>
            <Radio.Button value="BULK">Creación en Lote (Múltiples elementos)</Radio.Button>
          </Radio.Group>
        </div>
      )}

      <Form form={form} layout="vertical" initialValues={{ status: "ACTIVE", entry_date: dayjs() }}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="category_option_id"
              label="Categoría de Trazabilidad"
              rules={[{ required: true, message: "Seleccione una categoría" }]}
            >
              <Select placeholder="Seleccionar categoría">
                {categories.map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="element_id"
              label="Producto Padre (Elemento Trazable)"
              help="Asocia este registro a un elemento trazable de tu catálogo"
            >
              <ItemSearchSelect
                placeholder="Buscar elemento trazable (nombre, sku, etc.)..."
                traceableOnly={true}
                initialItems={storageElements}
              />
            </Form.Item>
          </Col>
        </Row>

        {creationMode === "SINGLE" ? (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="identifier"
                label="Identificador Único (Chapeta / Serial / Placa / Lote)"
                rules={[{ required: true, message: "El identificador es requerido" }]}
              >
                <Input placeholder="Ej. CH-001, SN-98234, PLACA-XYZ" prefix={<BarcodeOutlined />} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="name" label="Nombre / Alias (Opcional)">
                <Input placeholder="Ej. Pinta, Laptop Dirección, Camioneta #2" />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Card
            size="small"
            style={{ marginBottom: 16, backgroundColor: "#f0f5ff", borderColor: "#adc6ff" }}
            title={<Text strong>Ingreso de Múltiples Identificadores</Text>}
          >
            <Alert
              type="info"
              showIcon
              message="Ingrese un identificador por línea. Cada línea creará una nueva instancia trazable con los parámetros comunes configurados."
              style={{ marginBottom: 12 }}
            />
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="bulk_identifiers"
                  label="Lista de Identificadores (uno por línea)"
                  rules={[{ required: true, message: "Ingrese los identificadores" }]}
                >
                  <TextArea
                    rows={5}
                    placeholder={"CH-001\nCH-002\nCH-003\nCH-004"}
                    style={{ fontFamily: "monospace" }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="bulk_name_prefix"
                  label="Prefijo de Nombre/Alias opcional (Ej: 'Animal', 'Unidad')"
                >
                  <Input placeholder="Ej. Ternero" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
              <Select>
                <Option value="ACTIVE">Activo / Disponible</Option>
                <Option value="IN_PRODUCTION">En Producción / Ciclo</Option>
                <Option value="IN_USE">En Uso / Asignado</Option>
                <Option value="MAINTENANCE">En Mantenimiento</Option>
                <Option value="SOLD">Vendido</Option>
                <Option value="DISPOSED">Baja / Descarte</Option>
                <Option value="RETIRED">Retirado</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item name="storehouse_id" label="Almacén / Bodega">
              <Select allowClear placeholder="Seleccionar bodega">
                {storehouses.map((st) => (
                  <Option key={st.id} value={st.id}>
                    {st.name} {st.alias ? `(${st.alias})` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item name="location" label="Ubicación Específica">
              <Input placeholder="Ej. Potrero 4, Piso 2, Rack A-3" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="entry_date" label="Fecha de Ingreso">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="group_id"
              label="Asignar a Grupo / Hato / Lote (Opcional)"
              help="Agrupa este elemento con otros para gestión colectiva"
            >
              <Select allowClear placeholder="Seleccionar grupo">
                {groups.map((g) => (
                  <Option key={g.id} value={g.id}>
                    {g.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Dynamic Domain Metadata */}
        <Divider orientation="left">
          <Text strong style={{ fontSize: 13, color: "#595959" }}>
            Atributos Específicos ({selectedCategory?.string_value || "General"})
          </Text>
        </Divider>

        {categoryKey === "ANIMAL" && (
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "breed"]} label="Raza / Variedad">
                <Input placeholder="Ej. Cebú Brahmán, Holstein" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "sex"]} label="Sexo">
                <Select allowClear placeholder="Seleccionar">
                  <Option value="M">Macho</Option>
                  <Option value="H">Hembra</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "weight_kg"]} label="Peso Inicial (kg)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="Ej. 320" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={["metadata", "mother_identifier"]} label="Madre (Chapeta / ID)">
                <Input placeholder="Ej. CH-MADRE-01" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={["metadata", "birth_date"]} label="Fecha de Nacimiento">
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {(categoryKey === "ASSET" || categoryKey === "EQUIPMENT" || categoryKey === "VEHICLE") && (
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "acquisition_cost"]} label="Costo de Adquisición ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "salvage_value"]} label="Valor Residual ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "useful_life_months"]} label="Vida Útil (meses)">
                <InputNumber style={{ width: "100%" }} min={1} placeholder="Ej. 36, 60" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "brand"]} label="Marca">
                <Input placeholder="Ej. Dell, Caterpillar, Toyota" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "model"]} label="Modelo">
                <Input placeholder="Ej. XPS 15, CAT 320" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metadata", "year"]} label="Año de Fabricación">
                <InputNumber style={{ width: "100%" }} min={1900} max={2100} placeholder="2024" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {categoryKey === "BATCH" && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name={["metadata", "manufacturing_date"]} label="Fecha de Fabricación">
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={["metadata", "expiration_date"]} label="Fecha de Vencimiento">
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name={["metadata", "notes"]} label="Notas Adicionales">
              <TextArea rows={2} placeholder="Observaciones, características particulares, etc." />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default RegisterTraceableInstanceModal;
